import Phaser from 'phaser';
import { fillVerticalGradient } from './themeTextures';

/** Keys of the textures the room draws with. All generated from Graphics at runtime — no image files. */
export const ROOM_TEXTURES = {
  sky: 'room-sky',
  ground: 'room-ground',
  solid: 'room-solid',
  oneWay: 'room-one-way',
  post: 'room-post',
} as const;

/** Tile sizes; the ground tile is taller than any ground strip so its grass lip appears once. */
export const ROOM_TILE = { ground: 64, groundHeight: 128, solid: 32, oneWay: 32 } as const;

/** Generates the room textures once per game (they survive scene restarts in the texture manager). */
export function ensureRoomTextures(scene: Phaser.Scene, viewHeight: number): void {
  const textures = scene.textures;
  if (textures.exists(ROOM_TEXTURES.sky)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // Sky: vertical gradient strip (banded — see fillVerticalGradient), tiled across the view.
  fillVerticalGradient(g, 0, 0, 64, viewHeight, 0xbcdcf5, 0xf3f1e8);
  g.generateTexture(ROOM_TEXTURES.sky, 64, viewHeight);
  g.clear();

  // Ground tile: soil with a grass lip and a darker seam.
  const gt = ROOM_TILE.ground;
  const gh = ROOM_TILE.groundHeight;
  g.fillStyle(0x8a6a4a, 1);
  g.fillRect(0, 0, gt, gh);
  g.fillStyle(0x7a5c3f, 1);
  for (let i = 0; i < 10; i++) g.fillRect((i * 23) % gt, 18 + ((i * 17) % (gh - 30)), 9, 4);
  g.fillStyle(0x6fbf55, 1);
  g.fillRect(0, 0, gt, 10);
  g.fillStyle(0x4f9a3d, 1);
  g.fillRect(0, 8, gt, 3);
  g.generateTexture(ROOM_TEXTURES.ground, gt, gh);
  g.clear();

  // Solid step: stone block.
  const st = ROOM_TILE.solid;
  g.fillStyle(0x9a9aa6, 1);
  g.fillRect(0, 0, st, st);
  g.lineStyle(2, 0x6e6e7a, 1);
  g.strokeRect(1, 1, st - 2, st - 2);
  g.fillStyle(0xb8b8c4, 1);
  g.fillRect(3, 3, st - 6, 5);
  g.generateTexture(ROOM_TEXTURES.solid, st, st);
  g.clear();

  // One-way plank: wood with a bright top edge.
  const ow = ROOM_TILE.oneWay;
  g.fillStyle(0xc98b4b, 1);
  g.fillRect(0, 0, ow, 20);
  g.fillStyle(0xe6b877, 1);
  g.fillRect(0, 0, ow, 4);
  g.fillStyle(0x9c6a35, 1);
  g.fillRect(0, 16, ow, 4);
  g.fillRect(ow / 2 - 1, 4, 2, 12);
  g.generateTexture(ROOM_TEXTURES.oneWay, ow, 20);
  g.clear();

  // Sign post.
  g.fillStyle(0x7a5a3a, 1);
  g.fillRect(6, 0, 4, 40);
  g.fillStyle(0xf7f3e3, 1);
  g.fillRect(0, 0, 16, 12);
  g.generateTexture(ROOM_TEXTURES.post, 16, 40);
  g.destroy();
}
